/**
 * Gate Pass register grid — Outward, Inward and Approvals.
 *
 * Outward/Inward fetch their own filtered list. Approvals does NOT: it renders the
 * queues the hub already loaded, so switching to it costs no request.
 *
 * Every row action comes from getGatePassActions(), which is permission-filtered —
 * a button the backend would reject is never rendered.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import InvDataGrid from '../InvDataGrid';
import StatusChip from '../StatusChip';
import ConfirmDialog from '../ConfirmDialog';
import FilterToolbar from '../FilterToolbar';
import SectionCard from '../SectionCard';
import GateOutDialog from './GateOutDialog';
import {
  getGatePasses,
  submitGatePass,
  approveGatePass,
  rejectGatePass,
  cancelGatePass,
  gateOutGatePass,
  gateInGatePass,
} from '../../api/gatePass';
import { getStores } from '../../api/stockLedger';
import { toInventoryList } from '../../api/inventory-request';
import {
  GATE_PASS_PURPOSES,
  GATE_PASS_PURPOSE_LABELS,
  GATE_PASS_STATUS_FILTER_OPTIONS,
  GATE_PASS_STAGE_LABELS,
  GATE_PASS_STATUS_LABELS,
} from '../../constants/gatePass';
import { storeDisplayName } from '../../utils/storeEntity';
import { filterStoresByAssignment } from '../../utils/assignedStores';
import { formatDate } from '../../utils';
import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import {
  getGatePassActions,
  pendingStageOf,
  isReturnable,
  GATE_PASS_ACTIONS as A,
} from '../../utils/gatePassEntity';
import { IPD_COLORS } from '../../theme';

const TAB_COPY = {
  outward: {
    title: 'Outward Gate Passes',
    subtitle: 'Material leaving hospital premises',
    empty: 'No outward gate passes found.',
  },
  inward: {
    title: 'Inward Gate Passes',
    subtitle: 'Material entering hospital premises',
    empty: 'No inward gate passes found.',
  },
  approvals: {
    title: 'Awaiting You',
    subtitle: 'Passes that need your approval, and approved passes ready to leave',
    empty: 'Nothing is waiting for you.',
  },
};

/** ConfirmDialog action keys → the permission each one needs. */
const CONFIRM_ACTION_PERMISSION = {
  submit: INV_ACTIONS.GATEPASS_SUBMIT,
  approve: INV_ACTIONS.GATEPASS_APPROVE_STORE,
  reject: INV_ACTIONS.GATEPASS_APPROVE_STORE,
  cancel: INV_ACTIONS.GATEPASS_CANCEL,
};

export default function GatePassListPanel({ tab, base, queues }) {
  const history = useHistory();
  const { can, myStoreId, myStoreIds } = useInvRole();

  const isApprovals = tab === 'approvals';
  const copy = TAB_COPY[tab] || TAB_COPY.outward;

  const [items, setItems] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(!isApprovals);
  const [error, setError] = useState(null);

  const storeLocked = myStoreIds.length === 1;
  const [storeId, setStoreId] = useState(myStoreId || '');
  const [statusFilter, setStatusFilter] = useState('');
  const [purposeFilter, setPurposeFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [confirmTarget, setConfirmTarget] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [acting, setActing] = useState(false);
  const [gateTarget, setGateTarget] = useState(null);
  const [gateMode, setGateMode] = useState('gate-out');

  useEffect(() => {
    getStores()
      .then(d => setStores(toInventoryList(d)))
      .catch(() => {});
  }, []);

  const storeName = useCallback((id) => {
    if (!id) return '—';
    const s = stores.find(r => (r.store_id || r.id) === id);
    return s ? storeDisplayName(s) : id;
  }, [stores]);

  const load = useCallback(async () => {
    if (isApprovals) return;
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 100, direction: tab === 'inward' ? 'INWARD' : 'OUTWARD' };
      if (storeId) params.store_id = storeId;
      if (statusFilter) params.status = statusFilter;
      if (purposeFilter) params.purpose = purposeFilter;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      // API: GET /inventory/gate-pass/
      const data = await getGatePasses(params);
      setItems(toInventoryList(data));
    } catch (e) {
      setError(e.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [isApprovals, tab, storeId, statusFilter, purposeFilter, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  // Approvals renders the hub's queues — one list, ordered so the work that needs a
  // decision sits above the work that only needs a gate. Memoised because the
  // columns memo below depends on it.
  const approvalQueue = queues?.approvalQueue;
  const gateQueue = queues?.gateQueue;
  const rows = useMemo(
    () => (isApprovals ? [...(approvalQueue || []), ...(gateQueue || [])] : items),
    [isApprovals, approvalQueue, gateQueue, items],
  );

  function refresh() {
    if (!isApprovals) load();
    // An approval or a gate-out also changes the counts behind the stat cards.
    queues?.reload?.();
  }

  function openConfirm(action, row) {
    setConfirmAction(action);
    setConfirmTarget(row);
    if (action === 'reject') setRejectReason('');
  }

  function closeConfirm() {
    setConfirmTarget(null);
    setConfirmAction(null);
    setRejectReason('');
  }

  async function runConfirm() {
    if (!confirmTarget || !confirmAction) return;
    const required = CONFIRM_ACTION_PERMISSION[confirmAction];
    if (required && !can(required)) return;
    setActing(true);
    try {
      const id = confirmTarget.id;
      const stage = pendingStageOf(confirmTarget);
      if (confirmAction === 'submit') {
        // API: POST /inventory/gate-pass/{id}/submit/
        await submitGatePass(id);
        toast.success('Gate pass sent for approval');
      } else if (confirmAction === 'approve') {
        // API: POST /inventory/gate-pass/{id}/approve/
        await approveGatePass(id, { stage });
        toast.success('Gate pass approved');
      } else if (confirmAction === 'reject') {
        // API: POST /inventory/gate-pass/{id}/reject/
        await rejectGatePass(id, { stage, rejection_reason: rejectReason.trim() });
        toast.success('Gate pass rejected');
      } else {
        // API: DELETE /inventory/gate-pass/{id}/
        await cancelGatePass(id);
        toast.success('Gate pass cancelled');
      }
      closeConfirm();
      refresh();
    } catch (e) {
      toast.error(e.message || 'Action failed');
    } finally {
      setActing(false);
    }
  }

  function openGate(row, mode) {
    setGateMode(mode);
    setGateTarget(row);
  }

  async function runGate(form) {
    if (!gateTarget) return;
    setActing(true);
    try {
      if (gateMode === 'gate-in') {
        // API: POST /inventory/gate-pass/{id}/gate-in/
        await gateInGatePass(gateTarget.id, form);
        toast.success('Gate in recorded');
      } else {
        // API: POST /inventory/gate-pass/{id}/gate-out/
        await gateOutGatePass(gateTarget.id, form);
        toast.success('Gate out recorded');
      }
      setGateTarget(null);
      refresh();
    } catch (e) {
      toast.error(e.message || 'Action failed');
    } finally {
      setActing(false);
    }
  }

  // Measure the text the chip actually renders, not the enum behind it. This is the one
  // grid that passes domain="gatePass", so PARTIALLY_RETURNED shows as "Partly Returned" —
  // sizing off the raw code gave every badge a block far wider than its label.
  const statusChipMinWidth = useMemo(() => {
    const labels = rows
      .map(r => (r.status ? GATE_PASS_STATUS_LABELS[r.status] || String(r.status).replace(/_/g, ' ') : ''))
      .filter(Boolean);
    if (!labels.length) return undefined;
    return Math.ceil(Math.max(...labels.map(l => l.length)) * 7 + 20);
  }, [rows]);

  const assignedStores = filterStoresByAssignment(stores, myStoreIds);
  const storeOptions = myStoreIds.length
    ? assignedStores.map(s => ({ value: s.store_id || s.id, label: storeDisplayName(s) }))
    : [
      { value: '', label: 'All stores' },
      ...stores.map(s => ({ value: s.store_id || s.id, label: storeDisplayName(s) })),
    ];

  const lockedStoreLabel = storeOptions.find(o => o.value === myStoreId)?.label || '';

  const columns = useMemo(() => {
    const cols = [
      { field: 'gate_pass_number', headerName: 'Gate Pass #', width: 180 },
      {
        field: 'gate_pass_datetime',
        headerName: 'Date',
        width: 110,
        valueFormatter: p => formatDate(p.value),
      },
      {
        field: 'returnable_flag',
        headerName: 'Type',
        width: 130,
        renderCell: p => (
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: isReturnable(p.row) ? IPD_COLORS.info.text : IPD_COLORS.textSecondary }}>
            {isReturnable(p.row) ? 'Returnable' : 'Non-returnable'}
          </Typography>
        ),
      },
      {
        field: 'purpose',
        headerName: 'Purpose',
        width: 140,
        valueFormatter: p => GATE_PASS_PURPOSE_LABELS[p.value] || p.value || '—',
      },
      { field: 'party_name', headerName: 'Party', width: 180 },
      {
        field: 'store_id',
        headerName: 'Store',
        width: 160,
        valueFormatter: p => storeName(p.value),
      },
    ];

    if (isApprovals) {
      // What this row needs from ME. Read from pendingStageOf, which prefers the
      // server's own pending_stage — so switching the HOD stage on in the service
      // needs no frontend release at all.
      cols.push({
        field: 'stage',
        headerName: 'Waiting for',
        width: 150,
        valueGetter: p => GATE_PASS_STAGE_LABELS[pendingStageOf(p.row)] || '—',
      });
    }

    cols.push({
      field: 'status',
      headerName: 'Status',
      width: 150,
      renderCell: p => <StatusChip status={p.value} domain="gatePass" minWidth={statusChipMinWidth} />,
    });

    cols.push({
      field: 'actions',
      headerName: '',
      width: 180,
      sortable: false,
      renderCell: (p) => {
        const actions = getGatePassActions(p.row, can);
        return (
          <Box sx={{ display: 'flex', gap: 0.25 }}>
            <Tooltip title="View">
              <IconButton
                size="small"
                onClick={() => history.push(`${base}/${p.row.id}`)}
                sx={{ color: IPD_COLORS.primary }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {actions.has(A.EDIT) && (
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={() => history.push(`${base}/${p.row.id}/edit`)}
                  sx={{ color: IPD_COLORS.info.text }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {actions.has(A.SUBMIT) && (
              <Tooltip title="Send for approval">
                <IconButton
                  size="small"
                  onClick={() => openConfirm('submit', p.row)}
                  sx={{ color: IPD_COLORS.primary }}
                >
                  <SendIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {actions.has(A.APPROVE) && (
              <Tooltip title="Approve">
                <IconButton
                  size="small"
                  onClick={() => openConfirm('approve', p.row)}
                  sx={{ color: IPD_COLORS.success.text }}
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {actions.has(A.REJECT) && (
              <Tooltip title="Reject">
                <IconButton
                  size="small"
                  onClick={() => openConfirm('reject', p.row)}
                  sx={{ color: IPD_COLORS.warning.text }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {actions.has(A.GATE_OUT) && (
              <Tooltip title="Record gate out">
                <IconButton
                  size="small"
                  onClick={() => openGate(p.row, 'gate-out')}
                  sx={{ color: IPD_COLORS.info.text }}
                >
                  <LogoutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {actions.has(A.GATE_IN) && (
              <Tooltip title="Record gate in">
                <IconButton
                  size="small"
                  onClick={() => openGate(p.row, 'gate-in')}
                  sx={{ color: IPD_COLORS.success.text }}
                >
                  <LoginIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {actions.has(A.CANCEL) && (
              <Tooltip title="Cancel">
                <IconButton
                  size="small"
                  onClick={() => openConfirm('cancel', p.row)}
                  sx={{ color: IPD_COLORS.error.text }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    });

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, history, isApprovals, statusChipMinWidth, storeName, can]);

  const confirmCopy = {
    submit: {
      title: 'Send for Approval',
      description: `Send "${confirmTarget?.gate_pass_number}" for approval?`,
      label: 'Send',
    },
    approve: {
      title: 'Approve Gate Pass',
      description: `Approve "${confirmTarget?.gate_pass_number}"? The material can then leave the premises.`,
      label: 'Approve',
    },
    reject: {
      title: 'Reject Gate Pass',
      description: `Reject "${confirmTarget?.gate_pass_number}"? The material stays inside.`,
      label: 'Reject',
    },
    cancel: {
      title: 'Cancel Gate Pass',
      description: `Cancel "${confirmTarget?.gate_pass_number}"? This cannot be undone.`,
      label: 'Cancel gate pass',
    },
  }[confirmAction] || {};

  return (
    <Box>
      <SectionCard title={copy.title} subtitle={copy.subtitle}>
        {!isApprovals && (
          <FilterToolbar>
            {storeLocked ? (
              <FilterToolbar.StaticValue label="Store" width={220} value={lockedStoreLabel} />
            ) : (
              <FilterToolbar.Filter
                label="Store"
                width={220}
                value={storeId}
                onChange={e => setStoreId(e.target.value)}
                options={storeOptions}
              />
            )}
            <FilterToolbar.Filter
              label="Purpose"
              width={170}
              value={purposeFilter}
              onChange={e => setPurposeFilter(e.target.value)}
              options={[
                { value: '', label: 'All purposes' },
                ...GATE_PASS_PURPOSES.map(v => ({ value: v, label: GATE_PASS_PURPOSE_LABELS[v] })),
              ]}
            />
            <FilterToolbar.Filter
              label="Status"
              width={160}
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              options={GATE_PASS_STATUS_FILTER_OPTIONS}
            />
            <FilterToolbar.DateFilter
              label="From"
              value={fromDate}
              onChange={e => setFromDate(e.target.value)}
            />
            <FilterToolbar.DateFilter
              label="To"
              value={toDate}
              onChange={e => setToDate(e.target.value)}
            />
          </FilterToolbar>
        )}

        <Typography variant="caption" sx={{ color: IPD_COLORS.textSecondary, display: 'block', mb: 1 }}>
          {rows.length} record{rows.length === 1 ? '' : 's'}
        </Typography>

        <InvDataGrid
          rows={rows}
          columns={columns}
          loading={isApprovals ? queues?.loading : loading}
          error={isApprovals ? queues?.error : error}
          onRetry={refresh}
          getRowId={r => r.id}
          emptyMessage={copy.empty}
        />
      </SectionCard>

      <ConfirmDialog
        open={Boolean(confirmTarget)}
        title={confirmCopy.title}
        description={confirmCopy.description}
        destructive={confirmAction === 'cancel'}
        requireReason={confirmAction === 'reject'}
        reasonLabel="Rejection reason"
        reasonValue={rejectReason}
        onReasonChange={setRejectReason}
        loading={acting}
        onConfirm={runConfirm}
        onCancel={closeConfirm}
        confirmLabel={confirmCopy.label}
      />

      <GateOutDialog
        open={Boolean(gateTarget)}
        gatePass={gateTarget}
        mode={gateMode}
        loading={acting}
        onConfirm={runGate}
        onCancel={() => setGateTarget(null)}
      />
    </Box>
  );
}
